import React from 'react';
import Image from '@plone/volto/components/theme/Image/Image';
import { MPFLink } from 'volto-ministerio-publico-federal/components/UI';
import { EditItemButton } from './EditItemButton';
import { ExternalLinkIcon } from './ExternalLinkIcon';
import type { OverlayBadge } from './helpers';
import type { HeadingLevel } from 'volto-ministerio-publico-federal/types/heading';

/** Tamanho do título: o card alto do mosaico pede um corpo maior. */
export type OverlayCardSize = 'hero' | 'compact';

interface OverlayCardProps {
  badge: OverlayBadge;
  title: string;
  description?: string;
  href?: string;
  external?: boolean;
  alt: string;
  /** Já resolvida por `getIniciativaImageProps`/`getDestaqueCarrosselImageProps`. */
  image?: { src: string } | null;
  size?: OverlayCardSize;
  /** Proporção/altura da posição — quem manda é a seção que usa o card. */
  className?: string;
  /** A imagem do primeiro card acima da dobra não deve entrar em lazy. */
  eager?: boolean;
  isEditMode?: boolean;
  /** Rótulo acessível do lápis, ex.: "Editar card: Título". */
  editLabel?: string;
  /** Presente só no editor: abre este card na barra lateral. */
  onEdit?: () => void;
  /**
   * Nível do heading do título. O card aparece sob seções de profundidade
   * diferente na capa, então quem monta a faixa é que define o nível.
   */
  as?: HeadingLevel;
  /**
   * Versão compacta abaixo de `md`: padding, tag, título, descrição e botão
   * menores, para o card caber ~2 por vez no carrossel do celular. Do `md` em
   * diante não muda nada.
   */
  dense?: boolean;
}

/* A foto é fundo, não conteúdo: fica atrás do véu e cresce um tico no hover,
   como nos cards de destaque. */
const IMAGE_CLASS =
  'h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:transform-none motion-reduce:transition-none';

/* Botão claro do design. */
const CTA_BASE =
  'mt-4 inline-flex w-fit items-center gap-2 rounded-lg bg-surface px-4 py-2 text-base font-medium text-primary dark:border dark:border-[color:var(--mpf-dark-action-border)] dark:bg-[color:var(--mpf-dark-action-bg)] dark:text-night-50';

/* Botão compacto abaixo de `md` (ver `dense`). */
const CTA_DENSE =
  'max-md:mt-3 max-md:gap-1 max-md:whitespace-nowrap max-md:px-1.5 max-md:py-1 max-md:text-sm';

/* O `after` estende o clique ao card inteiro — é o único link do card, então
   não há área clicável concorrente. */
const CTA_LINK = `${CTA_BASE} cursor-pointer transition-colors after:absolute after:inset-0 hover:bg-gray-100 dark:hover:bg-[color:var(--mpf-dark-action-bg-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/60`;

/**
 * Card de foto ao fundo sob um véu colorido, com pílula no topo e o bloco de
 * texto colado no rodapé. Compartilhado pelo mosaico "Iniciativas e Projetos"
 * e pelo carrossel "Destaques" — as duas faixas têm o mesmo card, muda só de
 * onde vem a categoria (escolha do editor lá, fixa aqui).
 *
 * A cor do véu e a da pílula vêm juntas no `badge`, e a mecânica de
 * empilhamento (foto → véu → conteúdo) está em `_capa-overlay-cards.scss`.
 */
export const OverlayCard: React.FC<OverlayCardProps> = ({
  badge,
  title,
  description,
  href,
  external,
  alt,
  image,
  size = 'compact',
  className,
  eager,
  isEditMode,
  editLabel,
  onEdit,
  as: Heading = 'h3',
  dense,
}) => (
  /* `bg-gray-800` é o fundo de quem ainda não subiu foto: sem ele o véu
     translúcido ficaria sobre o branco da página e a pílula perderia
     contraste. */
  /* Coluna flex, e não bloco: é o que deixa o conteúdo ocupar a altura do card
     por `flex-1` em vez de `height: 100%`. A porcentagem depende de o pai ter
     altura definida — dentro do Swiper, que reescreve o layout na
     inicialização, ela caía para `auto` e o rodapé do card vazava para fora do
     recorte. */
  <article
    className={`mpf-overlay-card group flex flex-col ${dense ? 'max-md:rounded-xl' : ''} rounded-2xl bg-gray-800 ${className ?? ''}`}
  >
    {onEdit && (
      <EditItemButton
        label={editLabel ?? `Editar card: ${title || 'sem título'}`}
        onClick={onEdit}
        className="absolute right-2 top-2"
      />
    )}

    {image && (
      <div className="mpf-overlay-card__media overflow-hidden">
        <Image
          {...image}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          className={IMAGE_CLASS}
        />
      </div>
    )}

    <div
      aria-hidden="true"
      className={`mpf-overlay-card__overlay ${badge.overlay}`}
    />

    {/* `justify-between` empurra a pílula para o topo e o texto para o pé do
        card, como no design; com pouco conteúdo o card não fica com o texto
        boiando no meio. */}
    <div
      className={`relative z-[2] flex flex-1 flex-col justify-between gap-6 p-4 sm:p-6 ${
        dense ? 'max-md:gap-3 max-md:p-3' : ''
      }`}
    >
      <span
        className={`inline-flex w-fit items-center rounded-full px-[14px] py-[5px] text-xs font-semibold text-white ${dense ? 'max-md:px-2 max-md:py-0.5' : ''} ${badge.pill}`}
      >
        {badge.label}
      </span>

      <div>
        {/* `hero` cai para 20px no mobile e no tablet: o bento do desktop só
            existe a partir de `lg` e o carrossel só existe abaixo dele, então
            o `lg:` sempre resolve para 24 no bento e o `text-reading` sempre
            para 20 no carrossel — nenhum pixel muda no desktop. Antes, o slide
            do celular ficava com título MAIOR que o do desktop. */}
        <Heading
          className={`line-clamp-3 font-semibold text-white ${
            size === 'hero' ? 'text-reading lg:text-h3' : 'text-reading'
          } ${dense ? 'max-md:line-clamp-2 max-md:text-sm' : ''}`}
        >
          {title}
        </Heading>

        {description && (
          <p
            className={`mt-2 line-clamp-3 text-base text-white/90 ${
              dense ? 'max-md:line-clamp-4 max-md:text-xs' : ''
            }`}
          >
            {description}
          </p>
        )}

        {/* Sem destino não há botão: um card que não leva a lugar nenhum não
            deve parecer clicável. No editor o botão é inerte, senão clicar
            tira quem edita da página. */}
        {href &&
          (isEditMode ? (
            <span className={`${CTA_BASE} ${dense ? CTA_DENSE : ''}`}>
              Saiba mais
              <ExternalLinkIcon className="h-4 w-4" />
            </span>
          ) : (
            <MPFLink
              href={href}
              aria-label={`Saiba mais: ${title || 'sem título'}`}
              openInNewTab={external || undefined}
              className={`${CTA_LINK} ${dense ? CTA_DENSE : ''}`}
            >
              Saiba mais
              <ExternalLinkIcon className="h-4 w-4" />
            </MPFLink>
          ))}
      </div>
    </div>
  </article>
);

export default OverlayCard;
